import { Model, modelRegistry } from './Model';
import { ClientTransactionBasisType, ClientTransactionType } from '../types';
import Printable from '../print/Printable';
import { documentHeader, documentLines, documentTable, factLine } from '../print/DocumentLayout';
import Money from '../utilities/Money';
import Store from '../wrappers/Store';
import { I18n } from '../wrappers/I18n';
import Client from './Client';
import moment from 'moment-timezone';
export class ClientTransaction extends Model {
    static entity = 'client_transactions';
    static TYPE_MANUAL = ClientTransactionType.manual;
    static TYPE_AUTO = ClientTransactionType.auto;
    static BASIS_SALE = ClientTransactionBasisType.sale;
    static BASIS_ORDER = ClientTransactionBasisType.order;
    static BASIS_SUMMARY = ClientTransactionBasisType.summary;
    static BASIS_REFUND = ClientTransactionBasisType.refund;
    static BASIS_ADVANCE = ClientTransactionBasisType.advance;
    static BASIS_CREDIT_NOTE = ClientTransactionBasisType.credit_note;
    static BASIS_PAYMENT = ClientTransactionBasisType.payment;
    static BASIS_WRITE_OFF = ClientTransactionBasisType.write_off;
    static BASIS_CLIENT_SALE = ClientTransactionBasisType.client_sale;
    static BASIS_CLIENT_ORDER = ClientTransactionBasisType.client_order;
    static BASIS_CLIENT_SUMMARY = ClientTransactionBasisType.client_summary;
    static BASIS_CLIENT_REFUND = ClientTransactionBasisType.client_refund;
    static BASIS_CLIENT_ADVANCE = ClientTransactionBasisType.client_advance;
    static BASIS_CLIENT_CREDIT_NOTE = ClientTransactionBasisType.client_credit_note;
    static BASIS_CLIENT_PAYMENT = ClientTransactionBasisType.client_payment;
    static BASIS_CLIENT_WRITE_OFF = ClientTransactionBasisType.client_write_off;
    id = null;
    client_id = null;
    order_id = null;
    number = null;
    document_number = null;
    type = null;
    basis = null;
    date = null;
    due_date = null;
    debt = '0.0000';
    credit = '0.0000';
    balance = '0.0000';
    note = null;
    confirmed = true;
    created_at = null;
    updated_at = null;
    client;
    static fields() {
        return {
            id: this.attr(null),
            client_id: this.attr(null),
            order_id: this.attr(null),
            number: this.attr(null),
            document_number: this.attr(null),
            type: this.attr(null),
            basis: this.attr(null),
            date: this.attr(null),
            due_date: this.attr(null),
            debt: this.attr('0.0000', value => this.round(value, 4).toFixed(4)),
            credit: this.attr('0.0000', value => this.round(value, 4).toFixed(4)),
            balance: this.attr('0.0000', value => this.round(value, 4).toFixed(4)),
            note: this.attr(null),
            confirmed: this.attr(true, value => !!value),
            created_at: this.attr(null),
            updated_at: this.attr(null),
            client: this.attr(undefined, value => value ? new Client(value) : undefined),
        };
    }
    constructor(record) {
        super();
        this.$fill(record);
    }
    getDebt() {
        return Money.parse(this.debt);
    }
    getCredit() {
        return Money.parse(this.credit);
    }
    getBalance() {
        return Money.parse(this.balance);
    }
    static getPrintable(items, client, fromDate, toDate) {
        const pr = new Printable();
        const venue = Store.get('tenant')?.venue;
        if (!venue)
            return pr;
        documentHeader(pr, {
            title: I18n.t('client_transactions.heading'),
            facts: [
                factLine([I18n.t('general.client'), client.name]),
                `${moment(fromDate).format('DD.MM.YYYY.')} - ${moment(toDate).format('DD.MM.YYYY.')}`,
            ].filter(Boolean).join('     '),
            venueName: venue.name,
            venueLine: `${I18n.t('general.tax_id_number')}: ${venue.tax_id_number}`,
        });
        documentLines(pr, [factLine([I18n.t('client_transactions.balance'), items[items.length - 1].getBalance().toFormat('0.00')])]);
        const columns = [
            { label: `${I18n.t('general.number')} / ${I18n.t('general.date')}`, kind: 'index' },
            { label: I18n.t('client_transactions.due_date'), kind: 'index' },
            { label: I18n.t('client_transactions.basis.heading'), kind: 'name' },
            // a fiscal sale's number carries its counter and SEF id: "…-1234 (1234/1300ПП) (12345678)"
            { label: I18n.t('client_transactions.document_number'), kind: 'name' },
            { label: I18n.t('client_transactions.debt'), kind: 'figure' },
            { label: I18n.t('client_transactions.credit'), kind: 'figure' },
            { label: I18n.t('client_transactions.balance'), kind: 'figure' },
        ];
        const rows = items.map(item => [
            `${item.number} / ${item.date ? moment(item.date).format('DD.MM.YYYY') : ''}`,
            item.due_date ? moment(item.due_date).format('DD.MM.YYYY') : '',
            I18n.t(`client_transactions.basis.${item.basis}`),
            item.document_number ?? '',
            item.getDebt().toFormat('0.00'),
            item.getCredit().toFormat('0.00'),
            item.getBalance().toFormat('0.00'),
        ]);
        documentTable(pr, columns, rows, { density: 'regular' });
        return pr;
    }
}
export default ClientTransaction;
modelRegistry.register('ClientTransaction', ClientTransaction);
//# sourceMappingURL=ClientTransaction.js.map